import React, { useEffect, useState } from 'react';
import { Alert, Button, Container, List, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOpenHcpActivation } from '../../../global/entities/HcpActivation/queries/useOpenHcpActivation.tsx';
import { useSendHcpActivationCode } from '../../../global/entities/HcpActivation/mutations/useSendHcpActivationCode.tsx';
import { useVerifyHcpActivationCode } from '../../../global/entities/HcpActivation/mutations/useVerifyHcpActivationCode.tsx';
import { useCompleteHcpActivation } from '../../../global/entities/HcpActivation/mutations/useCompleteHcpActivation.tsx';
import { useCompleteSignIn } from '../../../global/hooks/useCompleteSignIn.ts';
import { ClinicSelectorModal } from '../ClinicSelector/ClinicSelectorModal.tsx';
import { errorCodeOf, formatWait, retryAfterSecondsOf, smsNumberOf } from '../../../global/helpers/smsCodeErrors.ts';
import { AppMedError } from '../../../global/error/AppMedError.ts';

type Step = 'start' | 'code' | 'password';

const PASSWORD_RULES = [
    { name: 'length', pattern: /.{8,}/ },
    { name: 'uppercase', pattern: /[A-Z]/ },
    { name: 'lowercase', pattern: /[a-z]/ },
    { name: 'digit', pattern: /[0-9]/ },
    { name: 'special', pattern: /[^A-Za-z0-9]/ },
] as const;

const brokenPasswordRules = (password: string): string[] =>
    PASSWORD_RULES.filter((rule) => !rule.pattern.test(password)).map((rule) => rule.name);

const serverBrokenPasswordRules = (error: unknown): string[] => {
    const brokenRules = error instanceof AppMedError ? error.contextData?.brokenRules : undefined;
    return Array.isArray(brokenRules) ? brokenRules.filter((rule): rule is string => typeof rule === 'string') : [];
};

const ActivateAccount: React.FC = () => {
    const { lang } = useParams<{ lang?: string }>();
    const linkLanguage = lang && ['en', 'fr'].includes(lang) ? lang : undefined;
    // The page keeps the link's language even when the session provider then applies an older culture cookie app-wide.
    const { t, i18n } = useTranslation(undefined, { lng: linkLanguage });
    const { hash } = useLocation();

    useEffect(() => {
        if (linkLanguage) i18n.changeLanguage(linkLanguage);
    }, [linkLanguage, i18n]);
    const token = hash.replace(/^#/, '');
    const activation = useOpenHcpActivation(token);
    const sendCode = useSendHcpActivationCode();
    const verifyCode = useVerifyHcpActivationCode();
    const complete = useCompleteHcpActivation();
    const { pendingUser, completeSignIn, selectClinic } = useCompleteSignIn();
    const [step, setStep] = useState<Step>('start');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [highlightedRules, setHighlightedRules] = useState<string[]>([]);
    const stopped = activation.data?.stopped || errorCodeOf(verifyCode.error) === 'TooManyAttempts';

    const retryAfterSeconds = retryAfterSecondsOf(sendCode.error);

    const handleSendCode = async () => {
        try {
            await sendCode.mutateAsync({ token });
            setStep('code');
        } catch {
            // the messages below come from the mutation state
        }
    };

    const sendCodeError = errorCodeOf(sendCode.error);
    const deliveryMessage =
        sendCodeError === 'SmsBlocked'
            ? t('activateAccount.smsBlocked', { number: smsNumberOf(sendCode.error) })
            : sendCodeError === 'CodeNotSent'
              ? t('activateAccount.codeNotSent')
              : undefined;

    const sendCodeMessages = (
        <>
            {retryAfterSeconds !== undefined && (
                <Text size="sm" c="red">
                    {t('activateAccount.waitBeforeNewCode', { time: formatWait(retryAfterSeconds) })}
                </Text>
            )}
            {deliveryMessage && (
                <Text size="sm" c="red" role="alert">
                    {deliveryMessage}
                </Text>
            )}
        </>
    );

    const handleVerifyCode = async () => {
        try {
            await verifyCode.mutateAsync({ token, code });
            setStep('password');
        } catch {
            // the error message below comes from the mutation state
        }
    };

    const handleComplete = async () => {
        const brokenRules = brokenPasswordRules(password);
        setHighlightedRules(brokenRules);
        if (brokenRules.length > 0) return;
        try {
            completeSignIn(await complete.mutateAsync({ token, password }));
        } catch (error) {
            setHighlightedRules(serverBrokenPasswordRules(error));
        }
    };

    const renderStep = () => {
        if (activation.isError || !token) {
            return (
                <Alert icon={<IconX size={16} />} color="red">
                    {t('activateAccount.linkNoLongerValid')}
                </Alert>
            );
        }

        if (stopped) {
            return (
                <Alert icon={<IconX size={16} />} color="red">
                    {t('activateAccount.tooManyAttempts')}
                </Alert>
            );
        }

        if (step === 'password') {
            return (
                <Stack gap="sm" w="100%">
                    <PasswordInput
                        label={t('activateAccount.password')}
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        error={
                            errorCodeOf(complete.error) === 'SamePassword' ? (
                                <span role="alert">{t('activateAccount.samePassword')}</span>
                            ) : undefined
                        }
                    />
                    <Text size="sm">{t('activateAccount.passwordRules')}</Text>
                    <List size="sm">
                        {PASSWORD_RULES.map((rule) => {
                            const broken = highlightedRules.includes(rule.name);
                            return (
                                <List.Item key={rule.name} data-state={broken ? 'broken' : 'met'} c={broken ? 'red' : undefined}>
                                    {t(`activateAccount.passwordRule.${rule.name}`)}
                                </List.Item>
                            );
                        })}
                    </List>
                    <Button loading={complete.isPending} onClick={handleComplete}>
                        {t('activateAccount.activate')}
                    </Button>
                </Stack>
            );
        }

        if (step === 'code') {
            return (
                <Stack gap="sm" w="100%">
                    <Text>{t('activateAccount.codeScreen', { last4: activation.data?.mobileLast4 })}</Text>
                    <TextInput
                        label={t('activateAccount.code')}
                        value={code}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, ''))}
                        error={verifyCode.isError ? <span role="alert">{t('activateAccount.codeInvalid')}</span> : undefined}
                    />
                    <Button loading={verifyCode.isPending} onClick={handleVerifyCode}>
                        {t('activateAccount.continue')}
                    </Button>
                    <Button variant="subtle" loading={sendCode.isPending} onClick={handleSendCode}>
                        {t('activateAccount.sendNewCode')}
                    </Button>
                    {sendCodeMessages}
                </Stack>
            );
        }

        return (
            <Stack gap="sm" align="center">
                <Button disabled={!activation.data} loading={sendCode.isPending} onClick={handleSendCode}>
                    {t('activateAccount.sendMeACode')}
                </Button>
                {sendCodeMessages}
            </Stack>
        );
    };

    return (
        <Container size="sm" style={{ paddingTop: '4rem' }}>
            <Paper p="xl" shadow="md" radius="md">
                <Stack gap="lg" align="center">
                    <Title order={2}>{t('activateAccount.title')}</Title>
                    {renderStep()}
                </Stack>
            </Paper>
            {pendingUser && pendingUser.healthcareFacilities && (
                <ClinicSelectorModal opened={!!pendingUser} facilities={pendingUser.healthcareFacilities} onSelect={selectClinic} />
            )}
        </Container>
    );
};

export default ActivateAccount;
